<template>
  <expand-transition slot="table">
    <div class="wro-actionable-panel__body">
      <sb-table
        :column-definitions="columnDefinitions"
        :rows="quotes"
        :loading-promise="quotesLoadingPromise"
      >
        <sb-table-thead slot="sb-table-header" slot-scope="{cols}">
          <sb-table-head>{{ cols[0].name }}</sb-table-head>
          <sb-table-head>{{ cols[1].name }}</sb-table-head>
          <sb-table-head>{{ cols[2].name }}</sb-table-head>
          <sb-table-head>{{ cols[3].name }}</sb-table-head>
        </sb-table-thead>
        <sb-table-row slot="sb-table-row" slot-scope="{row}">
          <sb-table-cell>{{ row.pricingGroupName }} </sb-table-cell>
          <sb-table-cell>{{ row.totalBoxes }}</sb-table-cell>
          <sb-table-cell>{{ row.totalWeight }}</sb-table-cell>
          <sb-table-cell v-if="!row.isError">
            <span v-if="showLocalCurrencyWithSymbolAndCode">
              {{ row.totalPrice | formatCurrency({currency: row.currencyCode, showCurrencyCode: true}) }}
            </span>
            <span v-else> {{ $n(row.totalPrice, 'currency') }} </span>
          </sb-table-cell>
          <sb-table-cell v-else class="wro-actionable-panel__error-weight">
            {{ row.totalPrice }}
          </sb-table-cell>
        </sb-table-row>
        <sb-table-empty-state
          slot="sb-table-empty-state"
        >
          <p>
            No Ship Option available for the specified weight and shipping zone
          </p>
        </sb-table-empty-state>
      </sb-table>
      <div class="wro-actionable-panel__body__label-type">
        <sb-field class="wro-actionable-panel__selection">
          <sb-label>Select which labels you'd like to purchase:</sb-label>
          <sb-dropdown
            id="decide-which-label"
            v-model="selectedPricingGroup"
            :options="dropdownAvailableOptions"
          >
            <sb-selected-option
              slot="selection"
              slot-scope="{ selected }"
            >
              {{ selected.name }}
            </sb-selected-option>
            <sb-options>
              <sb-option
                slot-scope="{ option }"
                :value="option"
              >
                {{ option.name }}
              </sb-option>
            </sb-options>
          </sb-dropdown>
        </sb-field>
        <estimated-arrival-date v-if="!isShipbobLabelSelected" v-model="estimatedArrivalDate" class="wro-actionable-panel__estimated" />
      </div>
    </div>
  </expand-transition>
</template>

<script>
import EstimatedArrivalDate from '../helpers/EstimatedArrivalDate.vue'
import { ShippingServiceCategory } from '@/enums/modules/shippingOptions'

const columnDefinitions = [
  { name: 'Carrier', field: 'carrierName' },
  { name: '# of boxes', field: 'numberOfBoxes' },
  { name: 'Total Weight (oz.)', field: 'totalWeight' },
  { name: 'Shipping Cost', field: 'shippingCost' }
]

export default {
  name: 'SelectLabel',
  components: {
    EstimatedArrivalDate
  },
  data: () => ({
    columnDefinitions,
    selectedVal: null,
    isShipbobLabelSelected: true,
    quotes: [],
    quotesLoadingPromise: null
  }),
  computed: {
    dropdownAvailableOptions () {
      const ddQuotes = (this.quotes && Array.isArray(this.quotes))
        ? this.quotes.map(x => ({
          id: x.id,
          name: x.pricingGroupName,
          estimatedShippingCost: (!x.isError) ? x.totalPrice : 'Exceeds weight limit.',
          shipbobZoneId: x.zone,
          currencyCode: x.currencyCode ?? 'USD'
        }))
        : []
      const sample = ddQuotes.filter(x => typeof x.estimatedShippingCost === 'number')
      sample.push({ name: 'I\'ll use my own labels' })
      return sample
    },
    estimatedArrivalDate () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/estimatedArrivalDate']
    },
    selectedPricingGroup: {
      get () {
        return this.$shipBobStore.getters['createWarehouseReceivingOrder/pricingGroup']
      },
      set (newVal) {
        this.$shipBobStore.dispatch('createWarehouseReceivingOrder/setPricingGroup', newVal)
      }
    },
    enableWROByShipOption () {
      return this.$shipBobStore.getters.enableWROByShipOption
    },
    showLocalCurrencyWithSymbolAndCode () {
      return this.enableWROByShipOption &&
      this.$shipBobStore.getters.showLocalCurrencyWithSymbolAndCode
    },
    getShippingServices () {
      const allShippingServices = this.$shipBobStore.getters['createWarehouseReceivingOrder/getShippingServices']
      const wroShippingServices = allShippingServices.filter(function (item) {
        return item.categoryId && item.categoryId === ShippingServiceCategory.WRO
      })
      return wroShippingServices
    }
  },
  watch: {
    selectedPricingGroup (n, o) {
      if (n.id) this.isShipbobLabelSelected = true
      else this.isShipbobLabelSelected = false
    }
  },
  async mounted () {
    this.quotesLoadingPromise = new Promise((resolve, reject) => {
      if (this.enableWROByShipOption === true && this.getShippingServices.length === 0) {
        resolve(this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getAllShippingServices', { loadingScopes: ['getWroQuotes'] }))
      } else {
        resolve()
      }
    })
    await this.quotesLoadingPromise
    if (this.enableWROByShipOption && this.getShippingServices.length > 0) {
      this.quotesLoadingPromise = new Promise((resolve, reject) => {
        resolve(
          this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getShipbobLabelQuotesByShipOption', { loadingScopes: ['getWroQuotes'] })
        )
      })
      await this.quotesLoadingPromise
      this.quotes = this.mapQuotesByShipOption(this.$shipBobStore.getters['createWarehouseReceivingOrder/getShippingLabelPricingByShipOption'])
    } else if (this.enableWROByShipOption === false) {
      this.quotesLoadingPromise = new Promise((resolve, reject) => {
        resolve(
          this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getShipbobLabelQuotes', { loadingScopes: ['getWroQuotes'] })
        )
      })
      await this.quotesLoadingPromise
      this.quotes = this.mapQuotes(this.$shipBobStore.getters['createWarehouseReceivingOrder/getShippingLabelPricing'])
    }
  },
  methods: {
    mapQuotes (val) {
      return (val && Array.isArray(val))
        ? val.map(x => ({
          id: x.pricingGroupId,
          pricingGroupName: x.pricingGroupName,
          totalPrice: (x.message.success) ? x.totalPrice : 'Exceeds weight limit.',
          totalWeight: x.totalWeight,
          totalBoxes: x.totalBoxes,
          isError: (!x.message.success),
          zone: x.zone,
          currencyCode: x.currencyCode ?? 'USD'
        }))
        : []
    },
    mapQuotesByShipOption (val) {
      return (val && Array.isArray(val))
        ? val.filter(x => x.totalPrice > 0).map(x => ({
          id: x.shipOptionId,
          pricingGroupName: this.getShippingServices.find(s => s.shippingServiceId === x.shipOptionId).description,
          totalPrice: (x.message.success) ? x.totalPrice : 'Exceeds weight limit.',
          totalWeight: x.totalWeight,
          totalBoxes: x.totalBoxes,
          isError: (!x.message.success),
          zone: x.zone,
          currencyCode: x.currencyCode
        }))
        : []
    }
  }
}
</script>

<style scoped lang='scss'>
.wro-actionable-panel {
  &__body {
    &__label-type {
      margin-top: 2rem;
      max-width: 50%;
    }
  }
  &__estimated {
    margin-top: 2rem;
    margin-left: 1.5rem;
  }
  &__error-weight {
    font-style: italic;
    color: red;
  }
}

</style>
